import React, { useState, useRef } from 'react';
import { FaCloudArrowUp, FaFile } from 'react-icons/fa6';
import Button from './atoms/Button';
import { useUploadImageMutation } from '../store/apis/common';
import { toastError } from '../utils/toasts';

interface FileUploadProps {
  className?: string;
  defaultFileUrl?: string | null;
  uploadButtonDisabled?: boolean;
  id?: string;
  onFileUploaded: (url: string) => void;
  onFileSelected?: (hasUnsavedFile: boolean) => void;
  maxSizeMB?: number;
  accept?: string[];
}

const FileUpload: React.FC<FileUploadProps> = ({
  className = '',
  defaultFileUrl,
  uploadButtonDisabled = false,
  id,
  onFileUploaded,
  onFileSelected,
  maxSizeMB = 5,
  accept = ['application/pdf'],
}) => {
  const [fileName, setFileName] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, resultsUploadFile] = useUploadImageMutation();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > maxSizeMB * 1024 * 1024) {
        toastError(`El tamaño máximo permitido es ${maxSizeMB}MB`);
        return;
      }

      setSelectedFile(file);
      setFileName(file.name);
      resultsUploadFile.reset();
      onFileSelected?.(true);
    }
  };

  const handleClick = () => {
    if (defaultFileUrl) {
      window.open(defaultFileUrl, '_blank');
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleClickUpload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (selectedFile) {
      try {
        const result = await uploadFile(selectedFile).unwrap();
        onFileUploaded(result);
        setSelectedFile(null);
        onFileSelected?.(false);
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
  };

  return (
    <div
      className={`relative cursor-pointer ${className} bg-surface-base rounded-lg`}
      onClick={handleClick}
    >
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept={accept.join(',')}
        onChange={handleFileSelect}
        id={id}
      />

      {fileName || defaultFileUrl ? (
        <div className="flex flex-col gap-2 border-2 border-dashed rounded-lg p-4">
          {defaultFileUrl && <span className="bg-surface-base/80 px-2 py-1 rounded text-text-primary text-sm font-medium">
            Comprobante, pulsá acá para verlo
          </span>}
          {!defaultFileUrl && <span className="bg-surface-base/80 px-2 py-1 rounded text-text-primary text-sm font-medium">
            {defaultFileUrl && !selectedFile ? 'Documento actual, pulsá acá para cambiarlo' : 'Documento seleccionado'}
          </span>}
          <div className="flex items-center gap-3 p-2">
            <FaFile className="text-red-600 text-3xl" />
            <span className="text-text-primary truncate max-w-[200px]">{fileName}</span>
          </div>
          {((!uploadButtonDisabled && !defaultFileUrl) || selectedFile) && (
            <div className="flex justify-center whitespace-nowrap mt-2">
              <Button
                onClick={handleClickUpload}
                disabled={resultsUploadFile.isLoading || resultsUploadFile.isSuccess}
                loading={resultsUploadFile.isLoading}
              >
                <span className="flex items-center gap-2 text-lg">
                  <div className="text-2xl"><FaCloudArrowUp /></div>
                  {resultsUploadFile.isSuccess ? 'Documento subido' : 'Subir'}
                </span>
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="w-full h-full min-h-[150px] border-2 border-dashed border-border-primary rounded-lg flex items-center justify-center">
          <div className="text-center p-4">
            <FaFile className="text-text-tertiary text-3xl mx-auto mb-2" />
            <p className="text-text-tertiary text-center">Sube un documento aquí</p>
            <p className="text-sm text-text-tertiary text-center">Documento hasta {maxSizeMB}MB</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
